import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import ImageUploader from '../../components/admin/ImageUploader';
import { adminV2Api } from '../../lib/api';
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, Crown, Loader2,
  ChevronUp, ChevronDown, AlertCircle, Eye, EyeOff, Sparkles, Calendar,
  Image as ImgIcon, ToggleLeft, ToggleRight, X,
} from 'lucide-react';

/**
 * /admin/events/new — create
 * /admin/events/:id/edit — edit existing
 *
 * Tabs/sections:
 *   • Basics      — title, slug, dates, venue, status
 *   • Content     — descriptions, artist block, images
 *   • Pass Types  — dynamic builder (add/remove/reorder, inline validation)
 *   • Danger      — soft delete (edit only)
 */

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const emptyPass = () => ({
  _tmpId: `tmp_${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  description: '',
  price: 0,
  inventory_total: 0,
  is_vip: false,
  requires_manual_approval: false,
  is_active: true,
  sort_order: 0,
});

const Tabs = ({ tabs, active, onChange }) => (
  <div className="editor-tabs" data-testid="editor-tabs">
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        onClick={() => onChange(t.key)}
        className={`editor-tab ${active === t.key ? 'editor-tab-active' : ''}`}
        data-testid={`editor-tab-${t.key}`}
      >
        {t.icon}
        <span>{t.label}</span>
      </button>
    ))}
  </div>
);

const Field = ({ label, hint, error, children, required }) => (
  <div className="editor-field">
    <label className="editor-field-label">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {hint && <p className="editor-field-hint">{hint}</p>}
    {children}
    {error && <p className="editor-field-error">{error}</p>}
  </div>
);

const Toggle = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`editor-toggle ${checked ? 'editor-toggle-on' : ''}`}
    data-testid={testid}
  >
    {checked ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
    <span>{label}</span>
  </button>
);


// ─── Pass Row ────────────────────────────────────────────────────────────────
const PassRow = ({ pass, idx, total, onChange, onRemove, onMove, errors }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22 }}
      className={`pass-builder-row ${pass.is_vip ? 'pass-builder-row-vip' : ''}`}
      data-testid={`pass-row-${idx}`}
    >
      <div className="pass-builder-handle">
        <GripVertical size={14} className="text-zinc-600" />
        <div className="pass-builder-move">
          <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0} aria-label="Move up" data-testid={`pass-up-${idx}`}>
            <ChevronUp size={12} />
          </button>
          <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === total - 1} aria-label="Move down" data-testid={`pass-down-${idx}`}>
            <ChevronDown size={12} />
          </button>
        </div>
      </div>

      <div className="pass-builder-fields">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 sm:col-span-6">
            <input
              type="text"
              value={pass.name}
              onChange={(e) => onChange(idx, 'name', e.target.value)}
              placeholder="Pass name (e.g. Couple, VIP Table)"
              className="editor-input"
              data-testid={`pass-name-${idx}`}
            />
            {errors?.name && <p className="editor-field-error mt-1">{errors.name}</p>}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <div className="editor-input-with-prefix">
              <span>₹</span>
              <input
                type="number"
                min="0"
                value={pass.price}
                onChange={(e) => onChange(idx, 'price', parseInt(e.target.value || '0', 10))}
                className="editor-input editor-input-prefixed"
                data-testid={`pass-price-${idx}`}
              />
            </div>
            {errors?.price && <p className="editor-field-error mt-1">{errors.price}</p>}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <input
              type="number"
              min="0"
              value={pass.inventory_total}
              onChange={(e) => onChange(idx, 'inventory_total', parseInt(e.target.value || '0', 10))}
              placeholder="Qty"
              className="editor-input"
              data-testid={`pass-inventory-${idx}`}
            />
            {errors?.inventory_total && <p className="editor-field-error mt-1">{errors.inventory_total}</p>}
          </div>
        </div>

        <input
          type="text"
          value={pass.description || ''}
          onChange={(e) => onChange(idx, 'description', e.target.value)}
          placeholder="Short description (optional)"
          className="editor-input mt-2"
          data-testid={`pass-desc-${idx}`}
        />

        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Toggle
            checked={pass.is_vip}
            onChange={(v) => {
              onChange(idx, 'is_vip', v);
              if (v) onChange(idx, 'requires_manual_approval', true);
            }}
            label="VIP"
            testid={`pass-vip-${idx}`}
          />
          <Toggle
            checked={pass.requires_manual_approval}
            onChange={(v) => onChange(idx, 'requires_manual_approval', v)}
            label="Manual Approval"
            testid={`pass-approval-${idx}`}
          />
          <Toggle
            checked={pass.is_active}
            onChange={(v) => onChange(idx, 'is_active', v)}
            label="Active"
            testid={`pass-active-${idx}`}
          />
          {pass.is_vip && <Crown size={12} className="text-[var(--accent-gold)] ml-1" />}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onRemove(idx)}
        className="pass-builder-remove"
        aria-label="Remove pass"
        data-testid={`pass-remove-${idx}`}
      >
        <Trash2 size={14} />
      </button>
    </motion.div>
  );
};


// ─── Main editor page ─────────────────────────────────────────────────────────
export default function EventEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [tab, setTab] = useState('basics');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    short_description: '',
    long_description: '',
    event_date: '',
    event_time: '21:00',
    venue: '',
    status: 'draft',
    hero_image: null,
    mobile_image: null,
    artist_block_enabled: false,
    artist_name: '',
    artist_description: '',
  });
  const [passes, setPasses] = useState([emptyPass()]);
  const [slugDirty, setSlugDirty] = useState(false);
  const [errors, setErrors] = useState({ event: {}, passes: {} });

  // ─── Hydrate on edit ────────────────────────────────────────────────
  const hydrate = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await adminV2Api.getEvent(id);
      const e = res.data || {};
      setForm({
        title: e.title || '',
        slug:  e.slug || '',
        short_description: e.short_description || '',
        long_description: e.long_description || '',
        event_date: e.event_date || '',
        event_time: e.event_time || '21:00',
        venue: e.venue || '',
        status: e.status || 'draft',
        hero_image: e.hero_image || null,
        mobile_image: e.mobile_image || null,
        artist_block_enabled: Boolean(e.artist_name || e.artist_description),
        artist_name: e.artist_name || '',
        artist_description: e.artist_description || '',
      });
      setPasses(
        (e.pass_types && e.pass_types.length > 0)
          ? e.pass_types.map((p) => ({
              _id: p._id, _existing: true,
              name: p.name || '',
              description: p.description || '',
              price: p.price || 0,
              inventory_total: p.inventory_total || 0,
              inventory_remaining: p.inventory_remaining,
              is_vip: Boolean(p.is_vip),
              requires_manual_approval: Boolean(p.requires_manual_approval),
              is_active: p.is_active !== false,
              sort_order: p.sort_order || 0,
            }))
          : [emptyPass()],
      );
      setSlugDirty(true);
    } catch (e) {
      toast.error('Could not load event');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, navigate]);

  useEffect(() => { hydrate(); }, [hydrate]);

  // ─── Field handlers ─────────────────────────────────────────────────
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTitleChange = (v) => {
    setField('title', v);
    if (!slugDirty) setField('slug', slugify(v));
  };
  const onSlugChange = (v) => { setSlugDirty(true); setField('slug', slugify(v)); };

  const updatePass = (idx, key, value) => {
    setPasses((arr) => arr.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  };
  const addPass = () => setPasses((arr) => [...arr, emptyPass()]);
  const removePass = (idx) => setPasses((arr) => arr.filter((_, i) => i !== idx));
  const movePass = (idx, dir) => {
    setPasses((arr) => {
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = arr.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  // ─── Validation ─────────────────────────────────────────────────────
  const validate = () => {
    const evErrs = {};
    const passErrs = {};
    if (!form.title.trim()) evErrs.title = 'Title is required.';
    if (!form.slug.trim()) evErrs.slug = 'Slug is required.';
    if (!form.event_date) evErrs.event_date = 'Event date is required.';
    if (passes.length === 0) evErrs.passes = 'At least one pass type is required.';
    passes.forEach((p, i) => {
      const e = {};
      if (!p.name.trim()) e.name = 'Name required';
      if (p.price < 0) e.price = 'Must be ≥ 0';
      if (p.inventory_total < 0) e.inventory_total = 'Must be ≥ 0';
      if (Object.keys(e).length > 0) passErrs[i] = e;
    });
    setErrors({ event: evErrs, passes: passErrs });
    return Object.keys(evErrs).length === 0 && Object.keys(passErrs).length === 0;
  };

  // ─── Save ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      // Jump to the first tab with an error
      if (errors.event?.title || errors.event?.slug || errors.event?.event_date) setTab('basics');
      else if (Object.keys(errors.passes || {}).length > 0) setTab('passes');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        short_description: form.short_description.trim(),
        long_description: form.long_description.trim(),
        event_date: form.event_date,
        event_time: form.event_time,
        venue: form.venue.trim(),
        status: form.status,
        hero_image: form.hero_image || null,
        mobile_image: form.mobile_image || null,
        artist_name: form.artist_block_enabled ? form.artist_name.trim() || null : null,
        artist_description: form.artist_block_enabled ? form.artist_description.trim() || null : null,
      };

      let eventId;
      if (isEdit) {
        const res = await adminV2Api.updateEvent(id, payload);
        eventId = res.data._id;
      } else {
        // Include pass_types in the initial create to seed them
        const createPayload = {
          ...payload,
          pass_types: passes.map((p, idx) => ({
            name: p.name.trim(),
            description: p.description?.trim() || '',
            price: p.price,
            inventory_total: p.inventory_total,
            is_vip: p.is_vip,
            requires_manual_approval: p.requires_manual_approval,
            is_active: p.is_active,
            sort_order: idx,
          })),
        };
        const res = await adminV2Api.createEvent(createPayload);
        eventId = res.data._id;
      }

      if (isEdit) {
        // Sync pass types: create new, update existing, delete missing
        const existing = await adminV2Api.getEvent(eventId);
        const existingIds = new Set((existing.data.pass_types || []).map((p) => p._id));
        const currentIds = new Set(passes.filter((p) => p._existing).map((p) => p._id));

        // Delete passes that were removed from the form
        for (const id of existingIds) {
          if (!currentIds.has(id)) {
            await adminV2Api.deletePassType(id);
          }
        }

        // Update existing, create new
        for (let i = 0; i < passes.length; i++) {
          const p = passes[i];
          const payload = {
            name: p.name.trim(),
            description: p.description?.trim() || '',
            price: p.price,
            inventory_total: p.inventory_total,
            is_vip: p.is_vip,
            requires_manual_approval: p.requires_manual_approval,
            is_active: p.is_active,
            sort_order: i,
          };
          if (p._existing && p._id) {
            await adminV2Api.updatePassType(p._id, payload);
          } else {
            await adminV2Api.createPassType(eventId, payload);
          }
        }
      }

      toast.success(isEdit ? 'Event updated' : 'Event created');
      navigate('/admin/events');
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete (edit only) ─────────────────────────────────────────────
  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this event? This is a soft delete — existing bookings remain intact.')) return;
    setDeleting(true);
    try {
      await adminV2Api.deleteEvent(id);
      toast.success('Event deleted');
      navigate('/admin/events');
    } catch (e) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Edit Event">
        <div className="events-loading">
          <Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" />
          <p>Loading…</p>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={isEdit ? `Edit · ${form.title || 'Event'}` : 'Create Event'}
      subtitle={isEdit ? 'Update details, passes, and inventory' : 'A new event in the V2 engine'}
    >
      <div className="event-editor" data-testid="event-editor-page">
        <div className="event-editor-toolbar">
          <Link to="/admin/events" className="event-back-btn" data-testid="editor-back">
            <ArrowLeft size={14} /> All events
          </Link>
          <div className="event-editor-toolbar-right">
            <button
              type="button"
              className={`status-toggle ${form.status === 'published' ? 'status-toggle-on' : ''}`}
              onClick={() => setField('status', form.status === 'published' ? 'draft' : 'published')}
              data-testid="editor-publish-toggle"
            >
              {form.status === 'published' ? <Eye size={13} /> : <EyeOff size={13} />}
              {form.status === 'published' ? 'Published' : 'Draft'}
            </button>
            <button
              type="button"
              className="editor-save-btn"
              onClick={save}
              disabled={saving}
              data-testid="editor-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Event')}
            </button>
          </div>
        </div>

        <Tabs
          tabs={[
            { key: 'basics',  label: 'Basics',     icon: <Calendar size={13} /> },
            { key: 'content', label: 'Content',    icon: <ImgIcon size={13} /> },
            { key: 'passes',  label: `Passes (${passes.length})`, icon: <Sparkles size={13} /> },
            ...(isEdit ? [{ key: 'danger', label: 'Danger Zone', icon: <AlertCircle size={13} /> }] : []),
          ]}
          active={tab}
          onChange={setTab}
        />

        <div className="event-editor-body">
          {/* ── BASICS ─────────────────────────────────────────────── */}
          {tab === 'basics' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="editor-section">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <Field label="Title" required error={errors.event.title}>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => onTitleChange(e.target.value)}
                      placeholder="e.g. Saturday Night Showcase"
                      className="editor-input"
                      data-testid="editor-title"
                    />
                  </Field>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Field label="Slug" hint="URL identifier — auto-generated, edit if needed." required error={errors.event.slug}>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => onSlugChange(e.target.value)}
                      placeholder="saturday-night-showcase"
                      className="editor-input editor-input-mono"
                      data-testid="editor-slug"
                    />
                  </Field>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Field label="Status">
                    <select
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                      className="editor-input"
                      data-testid="editor-status"
                    >
                      <option value="draft">Draft (hidden)</option>
                      <option value="published">Published (live)</option>
                      <option value="completed">Completed (archived)</option>
                    </select>
                  </Field>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Field label="Event Date" required error={errors.event.event_date}>
                    <input
                      type="date"
                      value={form.event_date}
                      onChange={(e) => setField('event_date', e.target.value)}
                      className="editor-input"
                      data-testid="editor-date"
                    />
                  </Field>
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Field label="Event Time" hint="24-hour format (HH:MM)">
                    <input
                      type="time"
                      value={form.event_time}
                      onChange={(e) => setField('event_time', e.target.value)}
                      className="editor-input"
                      data-testid="editor-time"
                    />
                  </Field>
                </div>
                <div className="col-span-12">
                  <Field label="Venue">
                    <input
                      type="text"
                      value={form.venue}
                      onChange={(e) => setField('venue', e.target.value)}
                      placeholder="Scratch Unleashed — Main Floor"
                      className="editor-input"
                      data-testid="editor-venue"
                    />
                  </Field>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── CONTENT ────────────────────────────────────────────── */}
          {tab === 'content' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="editor-section">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-7 space-y-4">
                  <Field label="Short Description" hint="Shown in cards and hero subtitle. 1–2 lines.">
                    <textarea
                      value={form.short_description}
                      onChange={(e) => setField('short_description', e.target.value)}
                      rows={2}
                      maxLength={240}
                      className="editor-input editor-textarea"
                      data-testid="editor-short-desc"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">{form.short_description.length}/240</p>
                  </Field>
                  <Field label="Long Description" hint="Full description on event detail page. Supports plain text & line breaks.">
                    <textarea
                      value={form.long_description}
                      onChange={(e) => setField('long_description', e.target.value)}
                      rows={8}
                      className="editor-input editor-textarea"
                      data-testid="editor-long-desc"
                    />
                  </Field>

                  <div className="editor-artist-block">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-white">Featured Artist Block</span>
                      <Toggle
                        checked={form.artist_block_enabled}
                        onChange={(v) => setField('artist_block_enabled', v)}
                        label={form.artist_block_enabled ? 'Enabled' : 'Disabled'}
                        testid="editor-artist-toggle"
                      />
                    </div>
                    {form.artist_block_enabled && (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={form.artist_name}
                          onChange={(e) => setField('artist_name', e.target.value)}
                          placeholder="Artist name"
                          className="editor-input"
                          data-testid="editor-artist-name"
                        />
                        <textarea
                          value={form.artist_description}
                          onChange={(e) => setField('artist_description', e.target.value)}
                          rows={3}
                          placeholder="Artist bio / credits"
                          className="editor-input editor-textarea"
                          data-testid="editor-artist-desc"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-5 space-y-4">
                  <ImageUploader
                    label="Hero Image (desktop)"
                    hint="Recommended: 1080 × 1350 (4:5), under 300 KB, WebP preferred."
                    value={form.hero_image}
                    onChange={(url) => setField('hero_image', url)}
                    aspect="4 / 5"
                    testid="editor-hero-upload"
                  />
                  <ImageUploader
                    label="Mobile Image (optional)"
                    hint="Optional override for mobile cards. Same 4:5 ratio recommended."
                    value={form.mobile_image}
                    onChange={(url) => setField('mobile_image', url)}
                    aspect="4 / 5"
                    testid="editor-mobile-upload"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* ── PASSES ─────────────────────────────────────────────── */}
          {tab === 'passes' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="editor-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold">Pass Types</h3>
                  <p className="text-xs text-zinc-500 mt-1">Add as many tiers as you need. Order is preserved.</p>
                </div>
                <button
                  type="button"
                  className="btn-gold-outline"
                  onClick={addPass}
                  data-testid="add-pass-btn"
                >
                  <Plus size={13} /> Add Pass
                </button>
              </div>

              {errors.event.passes && (
                <p className="editor-field-error mb-3">{errors.event.passes}</p>
              )}

              <AnimatePresence initial={false}>
                {passes.map((p, i) => (
                  <PassRow
                    key={p._id || p._tmpId || i}
                    pass={p}
                    idx={i}
                    total={passes.length}
                    onChange={updatePass}
                    onRemove={removePass}
                    onMove={movePass}
                    errors={errors.passes[i]}
                  />
                ))}
              </AnimatePresence>

              {passes.length === 0 && (
                <div className="editor-empty">
                  <p>No pass types yet.</p>
                  <button type="button" className="btn-gold mt-2" onClick={addPass}>
                    <Plus size={14} /> Add First Pass
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* ── DANGER ─────────────────────────────────────────────── */}
          {tab === 'danger' && isEdit && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="editor-section">
              <div className="editor-danger-card">
                <h3 className="text-red-300 font-semibold mb-2">Danger Zone</h3>
                <p className="text-sm text-zinc-400 mb-4">
                  Soft-delete this event. The event will be hidden from listings but existing bookings remain intact.
                </p>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid="editor-delete-btn"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {deleting ? 'Deleting…' : 'Delete Event'}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
